import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { useAnalytics } from 'src/components/settings/analytics';
import { useStatsigGate } from 'src/contexts/feature-provider';
import {
  SCROLL_TO_FILE,
  TOGGLE_DIFF_EXPANSION,
} from 'src/redux/pull-request/actions';
import { ITERATIVE_REVIEW_COMPLETED } from 'src/redux/pull-request/iterative-review-reducer';
import {
  getActiveDiff,
  getDiffFiles,
  getIsSingleFileModeActive,
  getPullRequestDiffFileCount,
  getPullRequestSourceHash,
} from 'src/redux/pull-request/selectors';
import { useParams } from 'src/router/hooks';
import { updateLastReviewedCommit } from 'src/sections/repository/sections/pull-request/components/iterative-review/utils';
import {
  useNextUnreviewedFileIdCallback,
  useNumFilesReviewed,
  useReviewedFiles,
  useSingleDiffReviewed,
} from 'src/sections/repository/sections/pull-request/components/reviewed-files/reviewed-files-provider';
import { getFileTreeHrefs } from 'src/selectors/file-tree-selectors';

export const useFileViewed = (): ((
  filePath: string,
  isReviewed: boolean,
  fileIndex: string[]
) => void) => {
  const dispatch = useDispatch();

  const navigateToNextFileFF = useStatsigGate(
    StatsigFeatureKeys.nextUnreviewedFile
  );

  const orderedHrefs = useSelector(getFileTreeHrefs);
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);
  const numTotalFiles = useSelector(getPullRequestDiffFileCount);

  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: number;
  }>();

  const sourceBranchHash = useSelector(getPullRequestSourceHash);

  const nextUnreviewedFileIdCallback = useNextUnreviewedFileIdCallback({
    orderedHrefs,
    isSingleFileModeActive,
  });
  const { toggleDiffReviewed } = useReviewedFiles();
  const { publishClickEvent } = useAnalytics();
  const numFilesViewed = useNumFilesReviewed();

  const onReviewed = useCallback(
    (filePath: string, isReviewed: boolean, fileIndex: string[]) => {
      toggleDiffReviewed(filePath, !isReviewed, fileIndex);
      const nextUnreviewedFileId = nextUnreviewedFileIdCallback(filePath);

      // SFM files are not collapsible, so we don't need to toggle expansion
      if (!isSingleFileModeActive) {
        dispatch({
          type: TOGGLE_DIFF_EXPANSION,
          payload: { filepath: filePath, isOpening: isReviewed },
        });
      }

      // navigate to next file in Single File Mode
      if (isSingleFileModeActive && !isReviewed && navigateToNextFileFF) {
        dispatch({
          type: SCROLL_TO_FILE,
          payload: nextUnreviewedFileId,
        });
      }

      // since numFilesViewed doesn't change within this function, we check if there is one file left to be reviewed
      // and if we are marking that last file as reviewed within this onChange function
      const allFilesViewed =
        numFilesViewed === numTotalFiles - 1 && !isReviewed;

      if (allFilesViewed) {
        updateLastReviewedCommit(
          sourceBranchHash,
          repositoryOwner,
          repositorySlug,
          pullRequestId
        );
        // Publish a success event to trigger sagas to mark the iterative review as completed.
        dispatch({
          type: ITERATIVE_REVIEW_COMPLETED.TRUE,
        });
      }

      publishClickEvent({
        name: 'markAsReviewedCheckbox',
        actionSubject: 'checkbox',
        attributes: {
          markedAsViewed: !isReviewed,
          allFilesViewed,
        },
      });
    },
    [
      dispatch,
      pullRequestId,
      repositoryOwner,
      repositorySlug,
      sourceBranchHash,
      nextUnreviewedFileIdCallback,
      isSingleFileModeActive,
      navigateToNextFileFF,
      numFilesViewed,
      numTotalFiles,
      publishClickEvent,
      toggleDiffReviewed,
    ]
  );

  return onReviewed;
};

// Wrapper for useFileViewed that operates on the currently highlighted file
export function useCurrentFileViewed(): () => void {
  const activeDiff = useSelector(getActiveDiff);
  const onFileViewed = useFileViewed();

  const filename = activeDiff.replace('chg-', '');
  const isReviewed = useSingleDiffReviewed(filename);
  const diffFiles = useSelector(getDiffFiles);
  const file = diffFiles.find(f => f.to === filename || f.from === filename);

  return useCallback(() => {
    if (file) {
      onFileViewed(filename, isReviewed, file.index);
    }
  }, [onFileViewed, isReviewed, file, filename]);
}
